import { Utils } from '../../utils/common'
import { DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { IdentityEvent, IdentityEvents, IIdentityEvent } from '../common/events/identity'
import { IProfile, Profile } from '../common/profile'
import { IIdentityProviderConfig, ISubscriberProviderConfig, ISubscriberServiceAdapter } from './adapters/adapter'

export interface IIdentityService {
    initialize(...args: any[]): void
    update(profile: IProfile, store: boolean): void
}

export class IdentityService extends SimpleEvents implements IIdentityService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            name: 'data-id-name',
            signIn: 'data-id-login',
            signOut: 'data-id-logout',
            signUp: 'data-id-signup'
        }
    }
    public globalStore: GlobalStore
    public profile: IProfile

    protected hashCode: number

    /**
     * Adds DOM event listeners.
     */
    public constructor(private adapter: ISubscriberServiceAdapter) {
        super()

        this.addListeners()

        // fetch profile from per-domain store, proceed anonymously if none found
        const localProfile = LocalStore.get<IProfile>(Store.ID)
        this.update(localProfile || new Profile()).catch(console.log)

        // attempt to fetch profile from global (cross-domain) store
        this.globalStore = new GlobalStore()
        this.globalStore.get<Profile>(Store.ID)
            .then((profile) => {
                // update relevant data if same user
                if (profile && profile.email === this.profile.email) {
                    this.update({
                        id: profile.id,
                        subscriptions: profile.subscriptions
                    } as IProfile).catch(console.log)
                }
            })
            .catch(console.log)
    }

    /**
     * Initializes adapter, adds listeners.
     *
     * @param {IdentityProviderConfig} ssoCreds for SSO provider
     * @param {IdentityProviderConfig} subscriberCreds for subscriptions provider
     */
    public initialize(ssoCreds: IIdentityProviderConfig, subscriberCreds: ISubscriberProviderConfig) {
        this.adapter
            .addEventListener(IdentityEvents.IDENTITY_CHANGE, (e: IIdentityEvent) => this.update(e.detail))
            // NOTE: traits are called first, so this corresponds to `WithPiano` signature
            .initialize(subscriberCreds, ssoCreds)

        // fire id init event
        this.dispatchEvent(IdentityEvent(IdentityEvents.IDENTITY_INIT, this.profile))

        return this
    }

    public async update(data: IProfile, dispatchEvent = true) {
        // profile unchanged or outdated, exit
        if (data.updated && this.profile && data.updated < this.profile.updated) {
            return
        }

        // merge signed-in or raw data
        this.profile = { ...this.profile, ...data }

        // profile changed
        if (!this.hashCode || Utils.hashCode(Utils.prune(this.profile)) !== this.hashCode) {

            // update modified time
            if (this.hashCode || !this.profile.updated) {
                this.profile.updated = Date.now()
            }

            // update hash (need to set before async actions so can ignore when no changes)
            this.hashCode = Utils.hashCode(Utils.prune(this.profile))

            // get subscriptions
            await this.updateSubscriptions()

            // update hash
            this.profile.updated = Date.now()
            this.hashCode = Utils.hashCode(Utils.prune(this.profile))

            // dispatch event
            if (dispatchEvent) {
                this.dispatchEvent(IdentityEvent(IdentityEvents.IDENTITY_CHANGE, this.profile))
            }

            // update UI
            this.updateUI().catch(console.log)

            // store
            this.storeProfile()
        }
    }

    /**
     * Registers listeners for required DOM events.
     *
     * TODO: expand as implementation requires.
     */
    private addListeners() {
        document.addEventListener('click', (e: Event) => {
            const el = e.target as Element

            if (el.hasAttribute(IdentityService.DOM.attrs.signIn)) {
                this.adapter.signIn()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.signOut)) {
                this.adapter.signOut()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.signUp)) {
                // etc.
            }
        })
    }

    /**
     * Sets name in UI (based on attr). Hides for anonymous users.
     *
     * @param {string} name
     */
    private async setName(name?: string) {
        await DOMUtils.ready;
        [...document.querySelectorAll(`[${IdentityService.DOM.attrs.name}]`)]
            .forEach((el) => {
                DOMUtils.setVis(el, !!name)
                el.innerHTML = name
            })
    }

    /**
     * Gets subscriptions.
     */
    private async updateSubscriptions() {
        if (this.profile.email) {
            if (this.profile.subscriptions === undefined) {
                this.profile.subscriptions = await this.adapter.getAccess()
            }
        } else {
            this.profile.subscriptions = undefined
        }
    }

    /**
     * Stores profile; global properties only (e.g. not 'adLight' since per-config)
     */
    private storeProfile() {
        // clean
        const profileStore = Utils.prune(this.profile)
        delete profileStore.adLight

        // store
        LocalStore.set(Store.ID, profileStore)
        this.globalStore.set(Store.ID, profileStore).catch(console.log)
    }

    /**
     * Updates UI (buttons etc.). Called after state change etc.
     */
    private async updateUI() {
        await DOMUtils.ready
        this.setName(this.profile.email ? this.profile.name : undefined).catch(console.log)

        DOMUtils.setVis(
            document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}], [${IdentityService.DOM.attrs.signUp}]`), !this.profile.email)
        DOMUtils.setVis(document.querySelectorAll(`[${IdentityService.DOM.attrs.signOut}]`), !!this.profile.email)
    }
}
